/**
 * PATH: packages/job-hub/src/jobs/tabs/JobItpLibrary.tsx
 * FILE_ID: JOBHUB-JOBS-ITP-LIBRARY-001
 * PURPOSE: Saved ITPs for a job as Locations and ITPs columns.
 * LANDMINE: The saved report stays mounted only while this component stays
 * mounted. A popup must be passed as `frame` around the list, not as a parent
 * of this component.
 */
import { useMemo, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FileText } from 'lucide-react'
import { createQaApi, type QaAreaExecution, type QaItpLibraryEntry } from '../qa/qaApi'
import { useJobHubDataAdapter } from '../jobHubDataAdapter'
import { QaItpExportView } from './job-qa/itp-export'
import { JobItpLibraryColumns } from './JobItpLibraryColumns'

export function ItpLibraryJump({ jobId, orgId }: { jobId: number; orgId?: number }) {
  const { client } = useJobHubDataAdapter()
  const api = useMemo(() => createQaApi(client, orgId), [client, orgId])
  const library = useQuery({
    queryKey: ['itp-library', jobId],
    queryFn: () => api.listItpLibrary(jobId),
  })
  const count = (library.data?.inspections ?? []).reduce((sum, row) => sum + row.entries.length, 0)
  return (
    <button
      type="button"
      onClick={() => document.getElementById('job-itp-library')?.scrollIntoView({ block: 'nearest' })}
      className="flex w-full shrink-0 items-center gap-2 rounded-lg border border-border p-3 text-left text-sm sm:w-56"
    >
      <FileText className="h-4 w-4" aria-hidden />
      <span>ITP Library</span>
      <span className="ml-auto text-xs tabular-nums text-muted-foreground">{count}</span>
    </button>
  )
}

export function JobItpLibrary({
  jobId,
  orgId,
  variant = 'overview',
  browsing = true,
  frame,
}: {
  jobId: number
  orgId?: number
  variant?: 'overview' | 'sheet'
  browsing?: boolean
  frame?: (list: ReactNode) => ReactNode
}) {
  const { client } = useJobHubDataAdapter()
  const api = useMemo(() => createQaApi(client, orgId), [client, orgId])
  const library = useQuery({
    queryKey: ['itp-library', jobId],
    queryFn: () => api.listItpLibrary(jobId),
    enabled: browsing,
  })
  const [opened, setOpened] = useState<{
    html: string
    inspectionId: string
    name: string
    entry: QaItpLibraryEntry
    executions: QaAreaExecution[]
  } | null>(null)
  const inspections = library.data?.inspections ?? []
  const count = inspections.reduce((sum, row) => sum + row.entries.length, 0)

  const openEntry = (inspectionId: string, entry: QaItpLibraryEntry) => {
    void Promise.all([
      api.getItpLibraryEntry(entry.id),
      api.listAreaExecutions(inspectionId, { photos: false }).catch(() => [] as QaAreaExecution[]),
    ]).then(([detail, executions]) => {
      setOpened({ html: detail.html, inspectionId, name: detail.name, entry, executions })
    })
  }

  const list = (
    <>
      {variant === 'overview' ? (
        <p className="text-xs text-muted-foreground uppercase tracking-wide mb-3">ITP Library</p>
      ) : null}
      {library.isLoading ? <p className="text-sm text-muted-foreground">Loading ITPs…</p> : null}
      {!library.isLoading && count === 0 ? (
        <p className="text-sm text-muted-foreground">No ITPs saved for this job.</p>
      ) : null}
      {count > 0 ? (
        <JobItpLibraryColumns
          inspections={inspections}
          capped={variant === 'overview'}
          onOpen={(row) => openEntry(row.inspectionId, row.entry)}
        />
      ) : null}
    </>
  )
  const padded = variant === 'sheet' ? <div className="px-4 py-2">{list}</div> : list
  const browser = frame
    ? frame(padded)
    : variant === 'sheet'
      ? padded
      : (
        <div id="job-itp-library" className="rounded-xl border border-border bg-card p-5 min-w-0">
          {padded}
        </div>
      )
  return (
    <>
      {browser}
      {opened ? (
        <QaItpExportView
          open
          onClose={() => setOpened(null)}
          api={api}
          jobId={jobId}
          orgId={orgId}
          inspectionId={opened.inspectionId}
          executions={opened.executions}
          jobNumber={jobId}
          request={{
            areaIds: opened.entry.area_ids,
            checklistIds: opened.entry.checklist_ids,
            itemIds: opened.entry.item_ids,
          }}
          defaultShowAll={false}
          reportTitle={opened.name}
          frozenHtml={opened.html}
        />
      ) : null}
    </>
  )
}
